import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Button,
  TextInput,
  Table,
  ActionIcon,
  Modal,
  Alert,
  Checkbox,
  Badge,
} from "@mantine/core";
import {
  IconQrcode,
  IconDownload,
  IconTrash,
  IconFileExport,
  IconCloudUpload,
  IconRefresh,
} from "@tabler/icons-react";
import {
  getStoredKeys,
  saveKey,
  deleteKey,
  backfillKey,
  type KeyPair,
} from "../lib/keys";
import { uploadKey, requestVerification, lookupByEmail } from "../lib/key-server";
import { CreateKeyModal } from "./CreateKeyModal";
import { ImportKeyModal } from "./ImportKeyModal";
import { KeyQrModal } from "./KeyQrModal";

export function KeyManagementView() {
  const [keys, setKeys] = useState<KeyPair[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // QR Modal State
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrKey, setQrKey] = useState<KeyPair | null>(null);

  // Delete Confirmation State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<KeyPair | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  // Import Modal State
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Export Modal State
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportKey, setExportKey] = useState<KeyPair | null>(null);
  const [exportWarningAccepted, setExportWarningAccepted] = useState(false);

  // Publishing state
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [keyToPublish, setKeyToPublish] = useState<KeyPair | null>(null);

  // Refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const loadKeys = async () => {
      const raw = getStoredKeys();
      const backfilled = await Promise.all(raw.map((k) => backfillKey(k)));
      setKeys(backfilled);
    };
    loadKeys();
  }, []);

  const handleKeyCreated = (newKey: KeyPair) => {
    saveKey(newKey);
    setKeys(getStoredKeys());
    setIsModalOpen(false);
  };

  const handleKeyImported = (newKey: KeyPair) => {
    saveKey(newKey);
    setKeys(getStoredKeys());
    setIsImportModalOpen(false);
  };

  const downloadKeypair = (key: KeyPair) => {
    const content = key.privateKeyArmored; // OpenPGP private keys contain the full keypair
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${key.name.replace(/\s+/g, "_")}_keypair.asc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleShowQr = (key: KeyPair) => {
    setQrKey(key);
    setQrModalOpen(true);
  };

  const handleExportClick = (key: KeyPair) => {
    setExportKey(key);
    setExportWarningAccepted(false);
    setExportModalOpen(true);
  };

  const handlePublishClick = (key: KeyPair) => {
    setKeyToPublish(key);
    setPublishModalOpen(true);
  };

  const confirmPublish = async () => {
    if (!keyToPublish) return;
    setPublishingId(keyToPublish.id);
    setPublishModalOpen(false);
    try {
      const { token } = await uploadKey(keyToPublish.publicKeyArmored);
      await requestVerification(token, keyToPublish.email);

      // Update local status
      keyToPublish.registryStatus = "pending";
      saveKey(keyToPublish);
      setKeys(getStoredKeys());

      alert(
        `Verification email sent to ${keyToPublish.email} from keys.openpgp.org. Check your junk folder if you don't see it. Once verified, refresh the keys screen.`,
      );
    } catch (e) {
      console.error("Publish failed:", e);
      alert("Failed to publish key to registry.");
    } finally {
      setPublishingId(null);
      setKeyToPublish(null);
    }
  };

  const handleRefreshPending = async () => {
    setIsRefreshing(true);
    let updatedCount = 0;
    try {
      const currentKeys = getStoredKeys();
      for (const k of currentKeys) {
        if (k.registryStatus === "pending") {
          const publicArmored = await lookupByEmail(k.email);
          if (publicArmored) {
            k.registryStatus = "published";
            saveKey(k);
            updatedCount++;
          }
        }
      }
      if (updatedCount > 0) {
        setKeys(getStoredKeys());
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDelete = (key: KeyPair) => {
    setKeyToDelete(key);
    setDeleteConfirmName("");
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (keyToDelete && deleteConfirmName === keyToDelete.name) {
      deleteKey(keyToDelete.id);
      setKeys(getStoredKeys());
      setDeleteModalOpen(false);
      setKeyToDelete(null);
      setDeleteConfirmName("");
    }
  };

  return (
    <div className="full-center-frame">
      <Box style={{ width: "100%", maxWidth: 1000 }}>
        <div className="premium-wrapper" style={{ maxHeight: "90vh" }}>
          <Group justify="space-between" mb="lg">
            <Group>
              <Title order={2}>Pass Keys</Title>
              <ActionIcon variant="light" onClick={handleRefreshPending} loading={isRefreshing} title="Refresh Pending Status">
                <IconRefresh size={16} />
              </ActionIcon>
            </Group>
            <Group>
              <Button
                variant="default"
                onClick={() => setIsImportModalOpen(true)}
              >
                Import Existing Key
              </Button>
              <Button onClick={() => setIsModalOpen(true)}>
                Create New Key
              </Button>
            </Group>
          </Group>

          {keys.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No keys found. Create one to start signing documents.
            </Text>
          ) : (
            <Box style={{ overflowX: "auto" }}>
              <Table verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name / Email</Table.Th>
                    <Table.Th>Key ID</Table.Th>
                    <Table.Th>Created / Expires</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {keys.map((k) => (
                    <Table.Tr key={k.id}>
                      <Table.Td>
                        <Text fw={500}>{k.name}</Text>
                        <Text size="xs" c="dimmed">
                          {k.email}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <Text size="xs" ff="monospace">
                            {k.fingerprint
                              ? `${k.fingerprint.slice(0, 4)}...${k.fingerprint.slice(-4)}`
                              : k.id}
                          </Text>
                          {k.registryStatus === "published" && (
                            <Badge size="xs" color="green" variant="light">
                              Published
                            </Badge>
                          )}
                          {k.registryStatus === "pending" && (
                            <Badge size="xs" color="yellow" variant="light">
                              Pending Verify
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {new Date(k.createdAt).toLocaleDateString()}
                        </Text>
                        {k.expiresAt && (
                          <Text
                            size="xs"
                            c={
                              new Date(k.expiresAt) < new Date()
                                ? "red"
                                : "dimmed"
                            }
                          >
                            Exp: {new Date(k.expiresAt).toLocaleDateString()}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group justify="flex-end" gap="xs">
                          {k.registryStatus !== "published" &&
                            k.registryStatus !== "pending" && (
                              <Button
                                variant="subtle"
                                size="xs"
                                color="blue"
                                onClick={() => handlePublishClick(k)}
                                loading={publishingId === k.id}
                                leftSection={<IconCloudUpload size={14} />}
                              >
                                Publish
                              </Button>
                            )}
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={() => handleExportClick(k)}
                            leftSection={<IconFileExport size={14} />}
                          >
                            Export
                          </Button>
                          <Button
                            variant="subtle"
                            color="red"
                            size="xs"
                            onClick={() => handleDelete(k)}
                            leftSection={<IconTrash size={14} />}
                          >
                            Delete
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          )}
        </div>
      </Box>

      <CreateKeyModal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={handleKeyCreated}
      />

      <ImportKeyModal
        opened={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImported={handleKeyImported}
      />

      <KeyQrModal
        opened={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        privateKeyArmored={qrKey?.privateKeyArmored || ""}
        keyName={qrKey?.name || ""}
      />

      <Modal
        opened={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        title="Export Identity Key"
        centered
      >
        <Stack>
          <Alert color="orange" title="Security Warning">
            <Text size="sm" mb="sm">
              Your private key secures your digital identity. Anyone who has
              this key can sign documents as you.
            </Text>
            <Checkbox
              label="The export of keys is only for moving keys to another of your devices that you control. Do not share these keys with others and do not use on a shared device."
              checked={exportWarningAccepted}
              onChange={(e) =>
                setExportWarningAccepted(e.currentTarget.checked)
              }
              color="orange"
            />
          </Alert>

          <Group grow>
            <Button
              disabled={!exportWarningAccepted}
              onClick={() => {
                setExportModalOpen(false);
                if (exportKey) handleShowQr(exportKey);
              }}
              leftSection={<IconQrcode size={16} />}
            >
              Show QR
            </Button>
            <Button
              disabled={!exportWarningAccepted}
              onClick={() => {
                setExportModalOpen(false);
                if (exportKey) downloadKeypair(exportKey);
              }}
              leftSection={<IconDownload size={16} />}
            >
              Download Keypair
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Key Deletion"
        centered
      >
        <Stack>
          <Alert color="red" variant="light">
            Warning: This action is permanent and cannot be undone. You will
            lose access to sign documents with this identity.
          </Alert>
          <Text size="sm">
            Are you sure you want to delete the key <b>{keyToDelete?.name}</b> (
            {keyToDelete?.email})?
          </Text>
          <Box>
            <Text size="xs" mb={4}>
              Please type <b>{keyToDelete?.name}</b> to confirm:
            </Text>
            <TextInput
              placeholder={keyToDelete?.name}
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  deleteConfirmName === keyToDelete?.name
                )
                  confirmDelete();
              }}
            />
          </Box>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmDelete}
              disabled={deleteConfirmName !== keyToDelete?.name}
            >
              Confirm Deletion
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        title="Publish to OpenPGP Registry"
        centered
      >
        <Stack>
          <Text size="sm">
            Your key will be sent to <b>keys.openpgp.org</b>, a trustworthy public repository for cryptographic keys.
            Only your <b>public key</b> is sent, never your private key.
          </Text>
          <Alert color="blue" title="Action Required">
            <Text size="sm">
              You will receive an email from <b>keys.openpgp.org</b> to verify your identity. <br />
              <b>Check your junk folder</b> if you do not see it. <br />
              Once verified, come back to this keys screen and click the refresh button.
            </Text>
          </Alert>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setPublishModalOpen(false)}>
              Cancel
            </Button>
            <Button color="blue" onClick={confirmPublish} loading={publishingId !== null}>
              Publish Key
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
